// Global 1/2 action mode — the SINGLE source of truth for the targeting
// reticle and the "defense vs positive" stance, consistent in EVERY
// environment (home + adventures). Three states:
//
//   OFF      — no reticle, neutral.
//   DEFENSE  — red crosshair. Home: shoot raiders. Adventure: guard (raid −60%).
//   POSITIVE — yellow ticks.  Home: heal / repair (hook for future). Adventure:
//              explore for treasure.
//
// Keys 1 and 2 each TOGGLE their own mode (press again → OFF). They're mutually
// exclusive, so pressing one while the other is on switches to it (the other
// turns off first). Only one reticle is ever shown — and it CAN be turned off.

import { Events, emit } from '../ui/eventBus.js'
import { ToolAutoOffMs } from '../config/balance.js'
import { showReticle, hideReticle } from '../ui/reticle.js'
import * as adv from './adventureManager.js' // runtime-only (skip auto-off mid-adventure)

export const ToolModes = Object.freeze({ OFF: 'OFF', DEFENSE: 'DEFENSE', POSITIVE: 'POSITIVE' })

// Auto-disarm: an armed target the player forgets fades + turns off after this much time WITHOUT BEING USED, and must be
// switched on again with its key. The red crosshair (key 1) is on a short leash; every shot restarts its clock.
const AUTO_OFF_MS = { [ToolModes.DEFENSE]: ToolAutoOffMs.DEFENSE, [ToolModes.POSITIVE]: ToolAutoOffMs.POSITIVE }
let autoOffTimer = null
function armAutoOff() {
  if (autoOffTimer) { clearTimeout(autoOffTimer); autoOffTimer = null }
  if (mode === ToolModes.OFF) return
  autoOffTimer = setTimeout(() => {
    if (!adv.isAdventuring?.()) setMode(ToolModes.OFF) // the reticle fades out, exactly as if you'd pressed the key
  }, AUTO_OFF_MS[mode] ?? 30_000)
}
// The player used the tool (fired a shot): restart the inactivity clock.
export function noteUsed() { armAutoOff() }

// Reticle styling per mode (shared singleton reticle).
const STYLE = {
  [ToolModes.DEFENSE]:  { color: '#ff4444', variant: 'crosshair', ring: 'solid' },
  [ToolModes.POSITIVE]: { color: '#ffd24a', variant: 'ticks',     ring: 'dashed' },
}

let mode = ToolModes.OFF

export function getToolMode() { return mode }
export function isDefense() { return mode === ToolModes.DEFENSE }
export function isPositive() { return mode === ToolModes.POSITIVE }

function setCanvasCursor(value) {
  const canvas = document.getElementById('scene')
  if (canvas) canvas.style.cursor = value
}

// Paint the reticle + cursor for the current mode.
function apply() {
  if (mode === ToolModes.OFF) {
    hideReticle()
    setCanvasCursor('')
  } else {
    showReticle(STYLE[mode])
    setCanvasCursor('crosshair')
  }
}

// Toggle a target mode on/off. Pressing the active mode's key clears it; any
// other state switches to the target (replacing the other mode).
export function toggle(target) {
  setMode(mode === target ? ToolModes.OFF : target)
}

// Set an explicit mode. No-op if unchanged or invalid.
export function setMode(next) {
  if (next !== ToolModes.OFF && next !== ToolModes.DEFENSE && next !== ToolModes.POSITIVE) return
  if (next === mode) return
  mode = next
  apply()
  // Restart the auto-disarm clock. While the cat's on an adventure the stance is
  // intentional for the trip, so don't auto-off there.
  armAutoOff()
  emit(Events.ToolModeChange, { mode })
}
